// @file Global confirmation dialog store
import { OzConfirmationDialogBoxButtonScheme } from '@@/library/v4/components/OzConfirmationDialogBox.vue'
import type { Wall, WallCamelCase } from '@@/types'
import { defineStore } from 'pinia'
import { ref } from 'vue'

enum ConfirmationDialogLayoutVariant {
  Small = 'Small',
  Large = 'Large',
  Drawer = 'Drawer',
}

interface ConfirmationDialogPayload {
  title: string
  subject?: null | { wall: Wall | WallCamelCase }
  layoutVariant?: ConfirmationDialogLayoutVariant
  iconSrc?: string | null
  body?: string
  customBodyClasses?: string
  confirmButtonText?: string
  secondaryActionButtonText?: string
  cancelButtonText?: string
  afterConfirmCallbacks: Array<() => void>
  afterCancelActions?: Array<() => void>
  afterSecondaryActionCallbacks?: Array<() => void>
  isCodeProtected?: boolean
  disableConfirmButton?: boolean
  buttonScheme?: OzConfirmationDialogBoxButtonScheme
  forceFullWidthButtons?: boolean
  xShadow?: boolean
  shouldFadeIn?: boolean
  xCancelButton?: boolean
  darkMode?: boolean
}

const defaultConfirmationDialogData: ConfirmationDialogPayload = {
  layoutVariant: ConfirmationDialogLayoutVariant.Small,
  subject: null,
  iconSrc: null,
  title: '',
  body: '',
  customBodyClasses: '',
  confirmButtonText: '',
  cancelButtonText: '',
  afterConfirmCallbacks: [],
  afterCancelActions: [],
  afterSecondaryActionCallbacks: [],
  isCodeProtected: false,
  disableConfirmButton: false,
  buttonScheme: OzConfirmationDialogBoxButtonScheme.Default,
  forceFullWidthButtons: false,
  xShadow: false,
  shouldFadeIn: true,
  xCancelButton: true,
  darkMode: undefined,
}

const useGlobalConfirmationDialogStore = defineStore('globalConfirmationDialog', () => {
  const isOpen = ref<boolean>(false)
  const confirmationDialogData = ref<ConfirmationDialogPayload>(defaultConfirmationDialogData)

  const openConfirmationDialog = (payload: ConfirmationDialogPayload): void => {
    isOpen.value = true
    confirmationDialogData.value = Object.assign({}, confirmationDialogData.value, payload)
  }

  const resetConfirmationDialog = (): void => {
    isOpen.value = false
    confirmationDialogData.value = Object.assign({}, confirmationDialogData.value, defaultConfirmationDialogData)
  }

  const executeActions = (actions?: Array<() => void>): void => {
    if (actions === undefined) return

    actions.forEach((action): void => {
      if (typeof action === 'function') {
        action()
      }
    })
  }

  const confirm = (): void => {
    executeActions(confirmationDialogData.value?.afterConfirmCallbacks)
    resetConfirmationDialog()
  }

  const cancel = (): void => {
    executeActions(confirmationDialogData.value?.afterCancelActions)
    resetConfirmationDialog()
  }

  const secondaryAction = (): void => {
    executeActions(confirmationDialogData.value?.afterSecondaryActionCallbacks)
    resetConfirmationDialog()
  }

  return {
    // State
    isOpen,
    confirmationDialogData,

    // Actions
    openConfirmationDialog,
    confirm,
    cancel,
    secondaryAction,
  }
})

export { ConfirmationDialogLayoutVariant, OzConfirmationDialogBoxButtonScheme, useGlobalConfirmationDialogStore }
export type { ConfirmationDialogPayload }
